import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/layout";
import {
  useListLocations,
  useUpdateLocation,
  useListLocationAvailability,
  useCreateLocationAvailability,
  useUpdateLocationAvailability,
  useDeleteLocationAvailability,
  useUpdateGymScheduleSync,
  useGetGymScheduleSync,
  getListLocationsQueryKey,
  getListLocationAvailabilityQueryKey,
  getGetGymScheduleSyncQueryKey,
  Location,
  LocationAvailabilityBlock,
} from "@workspace/api-client-react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { MapPin, Clock, Plus, Trash2, Building, Pencil, Link2, Bell, Ban, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { ChangePasswordCard } from "@/components/change-password-card";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const h = i.toString().padStart(2, "0");
  return { value: `${h}:00`, label: i === 0 ? "12:00 AM" : i < 12 ? `${i}:00 AM` : i === 12 ? "12:00 PM" : `${i - 12}:00 PM` };
});

type BlockFormState = { dayOfWeek: number; startTime: string; endTime: string };

const REMINDER_HOUR_OPTIONS = [
  { value: "1",   label: "1 hour before" },
  { value: "2",   label: "2 hours before" },
  { value: "4",   label: "4 hours before" },
  { value: "8",   label: "8 hours before" },
  { value: "12",  label: "12 hours before" },
  { value: "24",  label: "24 hours before (day-of)" },
  { value: "48",  label: "48 hours before (2 days)" },
  { value: "72",  label: "72 hours before (3 days)" },
];

const CANCEL_WINDOW_OPTIONS = [
  { value: "0",   label: "No penalty window (always free to cancel)" },
  { value: "2",   label: "2 hours before session" },
  { value: "4",   label: "4 hours before session" },
  { value: "8",   label: "8 hours before session" },
  { value: "12",  label: "12 hours before session" },
  { value: "24",  label: "24 hours before session" },
  { value: "48",  label: "48 hours before session (2 days)" },
  { value: "72",  label: "72 hours before session (3 days)" },
];

export default function AdminSettings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  const [blockForm, setBlockForm] = useState<BlockFormState>({ dayOfWeek: 1, startTime: "06:00", endTime: "21:00" });

  const [reminderHours, setReminderHours] = useState<string>("24");
  const [reminder2HrEnabled, setReminder2HrEnabled] = useState(true);
  const [reminderLoaded, setReminderLoaded] = useState(false);

  const [cancelWindowHours, setCancelWindowHours] = useState<string>("24");
  const [cancelWindowLoaded, setCancelWindowLoaded] = useState(false);

  const [noShowAlertThreshold, setNoShowAlertThreshold] = useState<string>("3");
  const [noShowAlertRatePct, setNoShowAlertRatePct] = useState<string>("30");
  const [noShowAlertLoaded, setNoShowAlertLoaded] = useState(false);

  const gymId = user?.gymId;
  const { data: syncData } = useGetGymScheduleSync(gymId || "", { query: { enabled: !!gymId } });

  const { data: gymData } = useQuery({
    queryKey: ["admin", "gym"],
    queryFn: async () => {
      const res = await fetch("/api/admin/gym", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch gym");
      return res.json();
    },
    enabled: !!gymId,
  });

  if (gymData && !reminderLoaded) {
    setReminderHours(String(gymData.sessionReminderHours ?? 24));
    setReminder2HrEnabled(gymData.sessionReminder2HrEnabled ?? true);
    setReminderLoaded(true);
  }

  if (gymData && !cancelWindowLoaded) {
    setCancelWindowHours(String(gymData.cancellationWindowHours ?? 24));
    setCancelWindowLoaded(true);
  }

  if (gymData && !noShowAlertLoaded) {
    setNoShowAlertThreshold(String(gymData.noShowAlertThreshold ?? 3));
    setNoShowAlertRatePct(String(gymData.noShowAlertRatePct ?? 30));
    setNoShowAlertLoaded(true);
  }

  const saveReminderMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/admin/gym/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          sessionReminderHours: parseInt(reminderHours, 10),
          sessionReminder2HrEnabled: reminder2HrEnabled,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error || "Failed to save reminder settings");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Reminder settings saved" });
      queryClient.invalidateQueries({ queryKey: ["admin", "gym"] });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const saveCancelWindowMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/admin/gym/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ cancellationWindowHours: parseInt(cancelWindowHours, 10) }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error || "Failed to save cancellation policy");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Cancellation policy saved" });
      queryClient.invalidateQueries({ queryKey: ["admin", "gym"] });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const saveNoShowAlertMutation = useMutation({
    mutationFn: async () => {
      const threshold = parseInt(noShowAlertThreshold, 10);
      const ratePct = parseInt(noShowAlertRatePct, 10);
      if (!Number.isFinite(threshold) || threshold < 1 || threshold > 50) throw new Error("Count threshold must be between 1 and 50");
      if (!Number.isFinite(ratePct) || ratePct < 0 || ratePct > 100) throw new Error("Rate threshold must be between 0 and 100");
      const res = await fetch("/api/admin/gym/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ noShowAlertThreshold: threshold, noShowAlertRatePct: ratePct }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error || "Failed to save no-show alert settings");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "No-show alert settings saved" });
      queryClient.invalidateQueries({ queryKey: ["admin", "gym"] });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const { data: locations = [] } = useListLocations(gymId || "", { query: { enabled: !!gymId } });
  const selectedLocation = locations.find((l: Location) => l.id === selectedLocationId) || locations[0];
  const activeLocationId = selectedLocation?.id;
  const primaryLocation = locations.find((l: Location) => l.isPrimary);

  const { data: availBlocks = [] } = useListLocationAvailability(activeLocationId || "", {
    query: { enabled: !!activeLocationId },
  });

  const updateLocationMut = useUpdateLocation();
  const createBlockMut = useCreateLocationAvailability();
  const updateBlockMut = useUpdateLocationAvailability();
  const deleteBlockMut = useDeleteLocationAvailability();
  const updateScheduleSyncMut = useUpdateGymScheduleSync();

  const scheduleSyncEnabled = syncData?.scheduleSyncEnabled ?? false;
  const isSelectedPrimary = selectedLocation?.isPrimary ?? false;
  const isSelectedSynced = selectedLocation?.useSharedSchedule && !isSelectedPrimary;
  const scheduleReadOnly = scheduleSyncEnabled && isSelectedSynced;

  const invalidateAll = () => {
    if (activeLocationId) {
      queryClient.invalidateQueries({ queryKey: getListLocationAvailabilityQueryKey(activeLocationId) });
    }
    if (gymId) {
      queryClient.invalidateQueries({ queryKey: getListLocationsQueryKey(gymId) });
    }
  };

  const invalidateBlocks = () => {
    if (activeLocationId) {
      queryClient.invalidateQueries({ queryKey: getListLocationAvailabilityQueryKey(activeLocationId) });
    }
  };

  const handleToggleScheduleSync = async (checked: boolean) => {
    if (!gymId) return;
    try {
      await updateScheduleSyncMut.mutateAsync({ id: gymId, data: { scheduleSyncEnabled: checked } });
      if (gymId) queryClient.invalidateQueries({ queryKey: getGetGymScheduleSyncQueryKey(gymId) });
      toast({ title: checked ? "Shared schedule enabled" : "Shared schedule disabled" });
    } catch {
      toast({ title: "Failed to update sync setting", variant: "destructive" });
    }
  };

  const handleToggleUseSharedSchedule = async (checked: boolean) => {
    if (!activeLocationId) return;
    try {
      await updateLocationMut.mutateAsync({ id: activeLocationId, data: { useSharedSchedule: checked } });
      if (gymId) queryClient.invalidateQueries({ queryKey: getListLocationsQueryKey(gymId) });
      invalidateBlocks();
      toast({ title: checked ? "Using shared schedule" : "Using custom schedule" });
    } catch {
      toast({ title: "Failed to update sync setting", variant: "destructive" });
    }
  };


  const handleAddBlock = async () => {
    if (!activeLocationId) return;
    try {
      await createBlockMut.mutateAsync({
        locationId: activeLocationId,
        data: { dayOfWeek: blockForm.dayOfWeek, startTime: blockForm.startTime, endTime: blockForm.endTime },
      });
      invalidateAll();
      setAddDialogOpen(false);
      setBlockForm({ dayOfWeek: 1, startTime: "06:00", endTime: "21:00" });
      toast({ title: "Time block added" });
    } catch {
      toast({ title: "Failed to add time block", variant: "destructive" });
    }
  };

  const handleEditBlock = async () => {
    if (!activeLocationId || !editingBlockId) return;
    try {
      await updateBlockMut.mutateAsync({
        locationId: activeLocationId,
        id: editingBlockId,
        data: { dayOfWeek: blockForm.dayOfWeek, startTime: blockForm.startTime, endTime: blockForm.endTime },
      });
      invalidateAll();
      setEditDialogOpen(false);
      setEditingBlockId(null);
      toast({ title: "Time block updated" });
    } catch {
      toast({ title: "Failed to update time block", variant: "destructive" });
    }
  };

  const openEditDialog = (block: LocationAvailabilityBlock) => {
    setEditingBlockId(block.id);
    setBlockForm({ dayOfWeek: block.dayOfWeek, startTime: block.startTime, endTime: block.endTime });
    setEditDialogOpen(true);
  };

  const handleDeleteBlock = async (blockId: string) => {
    if (!activeLocationId) return;
    try {
      await deleteBlockMut.mutateAsync({ locationId: activeLocationId, id: blockId });
      invalidateAll();
      toast({ title: "Time block removed" });
    } catch {
      toast({ title: "Failed to remove time block", variant: "destructive" });
    }
  };

  const blocksByDay = DAYS.map((_, i) =>
    availBlocks
      .filter((b: LocationAvailabilityBlock) => b.dayOfWeek === i)
      .sort((a: LocationAvailabilityBlock, b: LocationAvailabilityBlock) => a.startTime.localeCompare(b.startTime))
  );

  const formatTime = (t: string) => {
    const [h, m] = t.split(":").map(Number);
    const mm = String(m || 0).padStart(2, "0");
    if (h === 0) return `12:${mm} AM`;
    if (h < 12) return `${h}:${mm} AM`;
    if (h === 12) return `12:${mm} PM`;
    return `${h - 12}:${mm} PM`;
  };

  const renderBlockFormFields = () => (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <Label>Day of Week</Label>
        <Select value={String(blockForm.dayOfWeek)} onValueChange={(v) => setBlockForm(b => ({ ...b, dayOfWeek: Number(v) }))}>
          <SelectTrigger className="glass-panel border-white/10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DAYS.map((d, i) => (
              <SelectItem key={i} value={String(i)}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Start Time</Label>
          <Select value={blockForm.startTime} onValueChange={(v) => setBlockForm(b => ({ ...b, startTime: v }))}>
            <SelectTrigger className="glass-panel border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOURS.map(h => (
                <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>End Time</Label>
          <Select value={blockForm.endTime} onValueChange={(v) => setBlockForm(b => ({ ...b, endTime: v }))}>
            <SelectTrigger className="glass-panel border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOURS.map(h => (
                <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Mat Availability</h1>
          <p className="text-muted-foreground text-lg">
            Control when each location (mat) is available for private lessons.
          </p>
        </div>

        {locations.length > 1 && (
          <Card className="glass-panel border-white/5">
            <CardContent className="flex items-center justify-between p-4">
              <div>
                <p className="text-sm font-semibold text-white">Shared schedule across all locations</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  When enabled, editing the primary location's schedule automatically updates all synced locations.
                </p>
              </div>
              <Switch
                checked={scheduleSyncEnabled}
                onCheckedChange={handleToggleScheduleSync}
                disabled={updateScheduleSyncMut.isPending}
              />
            </CardContent>
          </Card>
        )}

        {locations.length === 0 ? (
          <Card className="glass-panel border-white/5">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <Building className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No locations found. Add locations to your gym first.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-primary" />
                <Select
                  value={activeLocationId || ""}
                  onValueChange={(val) => setSelectedLocationId(val)}
                >
                  <SelectTrigger className="w-[260px] glass-panel border-white/10">
                    <SelectValue placeholder="Select a location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc: Location) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        <div className="flex items-center gap-2">
                          {loc.name}
                          {loc.isPrimary && (
                            <Badge variant="outline" className="text-[9px] ml-1">Primary</Badge>
                          )}
                          {scheduleSyncEnabled && !loc.isPrimary && loc.useSharedSchedule && (
                            <Badge variant="secondary" className="text-[9px] ml-1">Synced</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedLocation && (
              <Card className="glass-panel border-white/5">
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      <div>
                        <CardTitle className="text-xl font-display uppercase tracking-wide text-white flex items-center gap-2">
                          {selectedLocation.name}
                          {selectedLocation.isPrimary && (
                            <Badge variant="outline" className="text-[10px]">Primary</Badge>
                          )}
                          {scheduleSyncEnabled && isSelectedSynced && (
                            <Badge variant="secondary" className="text-[10px]">Synced</Badge>
                          )}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {selectedLocation.address}, {selectedLocation.city}, {selectedLocation.state}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold uppercase tracking-wider text-primary">
                        Custom Schedule
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {scheduleSyncEnabled && !isSelectedPrimary && (
                    <div className="flex items-center justify-between p-3 mb-4 rounded-lg bg-white/[0.03] border border-white/5">
                      <div>
                        <p className="text-sm font-semibold text-white">Use shared schedule</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Mirror the primary location's hours. Turn off to set custom hours for this location.
                        </p>
                      </div>
                      <Switch
                        checked={selectedLocation.useSharedSchedule}
                        onCheckedChange={handleToggleUseSharedSchedule}
                        disabled={updateLocationMut.isPending}
                      />
                    </div>
                  )}

                  {scheduleReadOnly && (
                    <div className="flex items-center gap-3 py-4 justify-center text-muted-foreground border border-dashed border-white/10 rounded-xl mb-4">
                      <Link2 className="w-5 h-5 text-primary" />
                      <p>Time blocks synced with <span className="text-white font-semibold">{primaryLocation?.name || "the primary location"}</span>. Turn off shared schedule to edit independently.</p>
                    </div>
                  )}

                  {scheduleSyncEnabled && isSelectedPrimary && locations.length > 1 && (
                    <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-primary/5 border border-primary/20 text-sm text-muted-foreground">
                      <Link2 className="w-4 h-4 text-primary shrink-0" />
                      <p>Changes to this schedule will automatically apply to all synced locations in CUSTOM mode.</p>
                    </div>
                  )}

                  <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                          {scheduleReadOnly ? "Showing synced operating hours (read-only)." : "Define when this mat is open. Only trainer slots within these hours will be bookable."}
                        </p>
                        {!scheduleReadOnly && (
                          <Button size="sm" onClick={() => { setBlockForm({ dayOfWeek: 1, startTime: "06:00", endTime: "21:00" }); setAddDialogOpen(true); }} className="gap-1.5">
                            <Plus className="w-4 h-4" /> Add Hours
                          </Button>
                        )}
                      </div>

                      {availBlocks.length === 0 ? (
                        <div className="border border-dashed border-white/10 rounded-xl p-8 text-center text-muted-foreground">
                          <Clock className="w-8 h-8 mx-auto mb-3 opacity-50" />
                          <p>No hours defined yet.{!scheduleReadOnly && " All trainer slots will be blocked until you add open hours."}</p>
                        </div>
                      ) : (
                        <div className="grid gap-3">
                          {DAYS.map((dayName, dayIdx) => {
                            const dayBlocks = blocksByDay[dayIdx];
                            if (dayBlocks.length === 0) return null;
                            return (
                              <div key={dayIdx} className="flex items-start gap-4 p-3 rounded-xl bg-white/[0.02] border border-white/5">
                                <div className="w-20 shrink-0">
                                  <span className="text-sm font-bold text-white uppercase tracking-wider">{DAY_SHORT[dayIdx]}</span>
                                </div>
                                <div className="flex flex-wrap gap-2 flex-1">
                                  {dayBlocks.map((block: LocationAvailabilityBlock) => (
                                    <div
                                      key={block.id}
                                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20 text-sm"
                                    >
                                      <Clock className="w-3.5 h-3.5 text-primary" />
                                      <span className="font-mono text-white">
                                        {formatTime(block.startTime)} – {formatTime(block.endTime)}
                                      </span>
                                      {!scheduleReadOnly && (
                                        <>
                                          <button
                                            onClick={() => openEditDialog(block)}
                                            className="ml-1 text-muted-foreground hover:text-primary transition-colors"
                                          >
                                            <Pencil className="w-3.5 h-3.5" />
                                          </button>
                                          <button
                                            onClick={() => handleDeleteBlock(block.id)}
                                            className="text-muted-foreground hover:text-red-400 transition-colors"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="glass-panel border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-wide">Add Open Hours</DialogTitle>
          </DialogHeader>
          {renderBlockFormFields()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddBlock} disabled={createBlockMut.isPending}>
              {createBlockMut.isPending ? "Adding..." : "Add Hours"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="glass-panel border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-wide">Edit Open Hours</DialogTitle>
          </DialogHeader>
          {renderBlockFormFields()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEditBlock} disabled={updateBlockMut.isPending}>
              {updateBlockMut.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="max-w-2xl space-y-6">
        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-display text-xl uppercase tracking-wide">
              <Bell className="w-5 h-5 text-primary" />
              Session Reminders
            </CardTitle>
            <CardDescription>
              Automatically notify students and trainers before each confirmed session via email, SMS, and push.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Primary reminder window</Label>
              <Select value={reminderHours} onValueChange={setReminderHours}>
                <SelectTrigger className="glass-panel border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REMINDER_HOUR_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Both the student and trainer receive a reminder this far in advance.
              </p>
            </div>

            {parseInt(reminderHours, 10) > 2 && (
              <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-white">2-hour reminder</p>
                  <p className="text-xs text-muted-foreground">
                    Send an additional "starting soon" nudge 2 hours before the session.
                  </p>
                </div>
                <Switch checked={reminder2HrEnabled} onCheckedChange={setReminder2HrEnabled} />
              </div>
            )}

            <Button
              onClick={() => saveReminderMutation.mutate()}
              disabled={saveReminderMutation.isPending}
              className="w-full"
            >
              {saveReminderMutation.isPending ? "Saving..." : "Save Reminder Settings"}
            </Button>
          </CardContent>
        </Card>

        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-display text-xl uppercase tracking-wide">
              <Ban className="w-5 h-5 text-primary" />
              Cancellation Policy
            </CardTitle>
            <CardDescription>
              Set how far in advance a student must cancel to avoid a penalty. Both parties are always notified when a session is cancelled.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Penalty-free cancellation window</Label>
              <Select value={cancelWindowHours} onValueChange={setCancelWindowHours}>
                <SelectTrigger className="glass-panel border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CANCEL_WINDOW_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                If a student cancels within this window, both the student and trainer are notified that a penalty may apply.
                Trainer cancellations are never penalised.
              </p>
            </div>

            <Button
              onClick={() => saveCancelWindowMutation.mutate()}
              disabled={saveCancelWindowMutation.isPending}
              className="w-full"
            >
              {saveCancelWindowMutation.isPending ? "Saving..." : "Save Cancellation Policy"}
            </Button>
          </CardContent>
        </Card>

        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-display text-xl uppercase tracking-wide">
              <AlertTriangle className="w-5 h-5 text-primary" />
              No-Show Alert
            </CardTitle>
            <CardDescription>
              Get an email when a student crosses either threshold. Both conditions are checked independently — an alert fires as soon as either one is met.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Alert after this many no-shows</Label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={noShowAlertThreshold}
                  onChange={e => setNoShowAlertThreshold(e.target.value)}
                  className="w-24 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <span className="text-sm text-muted-foreground">no-show sessions</span>
              </div>
              <p className="text-xs text-muted-foreground">
                An alert fires the moment a student's no-show count reaches exactly this number. Default: 3.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Alert when no-show rate exceeds</Label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={noShowAlertRatePct}
                  onChange={e => setNoShowAlertRatePct(e.target.value)}
                  className="w-24 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <span className="text-sm text-muted-foreground">% of completed + no-show sessions</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Only applies once a student has at least 3 completed or no-show sessions. Default: 30%.
              </p>
            </div>

            <Button
              onClick={() => saveNoShowAlertMutation.mutate()}
              disabled={saveNoShowAlertMutation.isPending}
              className="w-full"
            >
              {saveNoShowAlertMutation.isPending ? "Saving..." : "Save No-Show Alert Settings"}
            </Button>
          </CardContent>
        </Card>

        <ChangePasswordCard />
      </div>
    </AppLayout>
  );
}
